import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { adminApiMessage } from '../adminApi';
import { EvalBadge, EvalError, RunLink } from '../evals/EvalBits';
import { modelShortName, verdictSourceText } from '../evals/format';
import {
  CASES_HREF,
  CLAIMS_MAX,
  CONSENTS_HREF,
  PROMPT_MAX,
  acceptDecision,
  candidateHref,
  candidateSignals,
  candidateTitle,
  caseIdPreview,
  draftFormFrom,
  draftProblems,
  draftSaveBody,
  EXPERT_CASE,
  expertsOffered,
  gateHref,
  gateOutcomeText,
  gateScores,
  inCorpusOutcome,
  isExpertAgent,
  isGateCandidate,
  kindLabel,
  momentOptionText,
  moveLabel,
  parseDuplicateTarget,
  refusalHref,
  routePreview,
  stateLabel,
  stateTone,
  targetAgentHint,
  targetAgentLabel,
  transcriptHref,
  turnExpertsText,
  withTargetAgent,
  type DraftForm,
} from '../evals/inbox';
import { inboxApi } from '../evals/inboxApi';
import {
  EVAL_CASE_EXPECTS,
  EVAL_CASE_KINDS,
  EVAL_CASE_TARGET_AGENTS,
  EVAL_GATE_QUESTIONS,
  type EvalCandidateDetail,
  type EvalTriageDecision,
  type EvalTriageState,
} from '../evals/inboxTypes';
import type { EvalVerdictSource } from '../evals/types';
import { GuideLink } from '../evals/GuideLink';
import { guideHref } from '../evals/guide';
import { formatDate } from '../format';

// One eval case candidate (TOM-140, ADR 0233): where it came from, its triage
// state and moves, and the reviewer's draft: the prompt in the reviewer's own
// words, the kind, the category, the claims a grader can answer yes or no, and
// the data it runs on, the stub boat unless the turn's boat has an active
// recorded-moment consent, and which agent should get it right: Neo, or an
// expert behind it whose own answer or edit was wrong (TOM-533, ADR 0342 §9).
// A case from a gate disagreement (TOM-314) goes to
// the input gate's labelled set instead: allow or refuse, and for refuse the
// gate questions that may refuse it, with no claims and no boat data. The page
// holds no chat text; the transcript, refusal and Neo → Gate links are the
// audited ways to read it. Every save and move is audited fail-closed on the
// server.

/** The promote endpoint's warning, carried onto the draft (ADR 0233 §4). */
const OUTLIVES_THE_USER_GATE =
  'This corpus outlives the user. Write the prompt in your own words, and remove anything personal: a name, a boat name, a place, contact details. The server never copies chat text into a draft.';
const OUTLIVES_THE_USER =
  'This corpus outlives the user. Write the prompt and the claims in your own words, and remove anything personal: a name, a boat name, a place, contact details. The server never copies chat text into a draft.';

function ClaimList({
  label,
  hint,
  lines,
  onChange,
  testId,
  disabled,
}: {
  label: string;
  hint: string;
  lines: string[];
  onChange: (lines: string[]) => void;
  testId: string;
  disabled: boolean;
}) {
  const filled = lines.filter((l) => l.trim().length > 0).length;
  return (
    <fieldset className="eval-inbox-claims" data-testid={testId} disabled={disabled}>
      <legend>
        {label}{' '}
        <span className="admin-muted eval-sub">
          {filled}/{CLAIMS_MAX}
        </span>
      </legend>
      <p className="admin-muted eval-sub">{hint}</p>
      {lines.map((line, i) => (
        <div key={i} className="eval-inbox-claim">
          <input
            type="text"
            value={line}
            maxLength={300}
            aria-label={`${label} ${i + 1}`}
            onChange={(e) => onChange(lines.map((l, j) => (j === i ? e.target.value : l)))}
          />
          <button
            type="button"
            className="admin-link-button"
            aria-label={`Remove ${label.toLowerCase()} ${i + 1}`}
            onClick={() => onChange(lines.length > 1 ? lines.filter((_, j) => j !== i) : [''])}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        className="admin-link-button"
        disabled={lines.length >= CLAIMS_MAX}
        onClick={() => onChange([...lines, ''])}
      >
        Add a line
      </button>
    </fieldset>
  );
}

function GateFacts({ g }: { g: NonNullable<EvalCandidateDetail['gate']> }) {
  return (
    <div className="eval-stack" data-testid="eval-candidate-gate">
      <span>{gateOutcomeText(g)}</span>
      <span className="admin-muted eval-sub">
        Jev answered through {g.decidedBy === 'typesafe' ? 'TypeSafe' : 'OpenRouter'}, gate{' '}
        <code>{g.gateVersion}</code>. Probability of yes per question:
      </span>
      <dl className="eval-kv eval-inbox-gate-scores">
        {gateScores(g).map((s) => (
          <div key={s.question} className="eval-inbox-gate-score">
            <dt>
              <code>{s.question}</code>
            </dt>
            <dd>{s.score === null ? '—' : s.score.toFixed(2)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Facts({ c }: { c: EvalCandidateDetail }) {
  const transcript = transcriptHref(c);
  const refusal = refusalHref(c);
  const gate = gateHref(c);
  const gateCase = isGateCandidate(c.sources);
  return (
    <article className="eval-card" data-testid="eval-candidate-facts">
      <dl className="eval-kv">
        <dt>Key</dt>
        <dd>
          <code>{c.key}</code>
        </dd>
        <dt>Signals</dt>
        <dd>
          <ul className="eval-inbox-signal-list">
            {candidateSignals(c).map((s) => (
              <li key={s.source}>
                <EvalBadge tone="neutral">{s.label}</EvalBadge>{' '}
                {s.count > 0 ? `${s.count} ${s.count === 1 ? 'signal' : 'signals'}` : 'recorded'}
                {s.details.length > 0 ? (
                  <span className="admin-muted"> · {s.details.join(', ')}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </dd>
        {c.gate !== null ? (
          <>
            <dt>Input gate</dt>
            <dd>
              <GateFacts g={c.gate} />
            </dd>
          </>
        ) : null}
        <dt>Prompt version</dt>
        <dd>{c.promptVersion !== null ? <code>{c.promptVersion}</code> : '—'}</dd>
        {c.messageId !== null ? (
          <>
            <dt>Experts in the turn</dt>
            <dd data-testid="eval-candidate-experts">{turnExpertsText(c.specialists)}</dd>
          </>
        ) : null}
        <dt>Happened</dt>
        <dd>{c.occurredAt !== null ? formatDate(c.occurredAt) : 'Its source was erased'}</dd>
        <dt>Read it</dt>
        <dd>
          {transcript !== null ? (
            <Link to={transcript} data-testid="eval-candidate-transcript">
              Open the transcript at this turn →
            </Link>
          ) : refusal !== null ? (
            <Link to={refusal} data-testid="eval-candidate-refusal">
              Open this refusal →
            </Link>
          ) : null}
          {gate !== null ? (
            <>
              {transcript !== null || refusal !== null ? ' · ' : null}
              <Link to={gate} data-testid="eval-candidate-gate-link">
                Open this disagreement in Neo → Gate →
              </Link>
            </>
          ) : null}
          {transcript === null && refusal === null && gate === null ? (
            <span className="admin-muted">Nothing left to read: its source was erased.</span>
          ) : null}
          {transcript !== null || refusal !== null || gate !== null ? (
            <span className="admin-muted eval-sub">
              {' '}
              Asks for a reason, and the read is audited.
            </span>
          ) : null}
        </dd>
        {c.promote !== null ? (
          <>
            <dt>Promoted as</dt>
            <dd>
              <code>{c.promote.caseType ?? '—'}</code>
              {c.promote.expected !== null ? (
                <span className="eval-stack eval-sub">
                  <span className="admin-muted">Expected (the promoter&apos;s words):</span>
                  <span>{c.promote.expected}</span>
                </span>
              ) : null}
              {c.promote.notes !== null ? (
                <span className="eval-stack eval-sub">
                  <span className="admin-muted">Notes:</span>
                  <span>{c.promote.notes}</span>
                </span>
              ) : null}
            </dd>
          </>
        ) : null}
        {c.exportedAt !== null ? (
          <>
            <dt>Exported</dt>
            <dd>{formatDate(c.exportedAt)}</dd>
          </>
        ) : null}
        <dt>In the corpus</dt>
        <dd data-testid="eval-candidate-in-corpus">
          {c.inCorpus === null ? (
            <span className="admin-muted">
              {gateCase
                ? 'A gate case stays accepted: the gate calibration stores no run, so nothing moves it to in corpus.'
                : 'Not yet. It moves to in corpus once a stored run holds results for its case id.'}
            </span>
          ) : (
            <span className="eval-stack">
              <span>
                <EvalBadge tone={inCorpusOutcome(c.inCorpus).tone}>
                  {inCorpusOutcome(c.inCorpus).text}
                </EvalBadge>{' '}
                <span className="admin-muted eval-sub">
                  latest, {verdictSourceText(c.inCorpus.latest.verdictSource as EvalVerdictSource)}
                </span>
              </span>
              <span className="eval-sub">
                Latest run <RunLink id={c.inCorpus.latest.runId} /> (
                {modelShortName(c.inCorpus.latest.model)}, {c.inCorpus.latest.suite} suite,{' '}
                {formatDate(c.inCorpus.latest.collectedAt)})
              </span>
              <span className="eval-sub">
                First run <RunLink id={c.inCorpus.firstRunId} /> on{' '}
                {formatDate(c.inCorpus.firstCollectedAt)}, {c.inCorpus.results}{' '}
                {c.inCorpus.results === 1 ? 'result' : 'results'} collected
              </span>
            </span>
          )}
        </dd>
      </dl>
    </article>
  );
}

function Triage({
  c,
  busy,
  onMove,
}: {
  c: EvalCandidateDetail;
  busy: boolean;
  onMove: (decision: EvalTriageDecision) => void;
}) {
  const [open, setOpen] = useState<null | 'rejected' | 'duplicate'>(null);
  const [reason, setReason] = useState('');
  const [target, setTarget] = useState('');
  const parsedTarget = parseDuplicateTarget(target);
  const moves = c.allowedMoves.filter((m) => m !== 'accepted');
  const lockedByExport = c.state === 'accepted' && c.exportedAt !== null;

  function submitReject(e: FormEvent) {
    e.preventDefault();
    if (reason.trim().length < 3) return;
    onMove({ to: 'rejected', reason: reason.trim() });
  }

  function submitDuplicate(e: FormEvent) {
    e.preventDefault();
    if (parsedTarget === null) return;
    onMove({ to: 'duplicate', of: parsedTarget });
  }

  return (
    <article className="eval-card" data-testid="eval-triage">
      <header className="eval-card-header">
        <h3>Triage</h3>
        <EvalBadge tone={stateTone(c.state)} testId="eval-triage-state">
          {stateLabel(c.state)}
        </EvalBadge>
      </header>
      {c.state === 'rejected' && c.rejectReason !== null ? (
        <p data-testid="eval-reject-reason">
          <span className="admin-muted">Rejected:</span> {c.rejectReason}
        </p>
      ) : null}
      {c.state === 'duplicate' && c.duplicateOf !== null ? (
        <p data-testid="eval-duplicate-of">
          <span className="admin-muted">A duplicate of </span>
          {'caseId' in c.duplicateOf ? (
            <code>{c.duplicateOf.caseId}</code>
          ) : (
            <Link to={candidateHref({ rowId: c.duplicateOf.rowId, key: c.duplicateOf.key ?? '' })}>
              {c.duplicateOf.key !== null
                ? candidateTitle({ key: c.duplicateOf.key })
                : `case row ${c.duplicateOf.rowId.slice(0, 8)}`}
            </Link>
          )}
        </p>
      ) : null}
      {c.state === 'in_corpus' ? (
        <p className="admin-muted">
          A stored run holds this case, so it is final. A fix is a change in git.
        </p>
      ) : null}
      {lockedByExport ? (
        <p className="eval-callout" data-testid="eval-exported-lock">
          This case went out in an export on {formatDate(c.exportedAt)} and may be in git, so it
          stays accepted here. Fix it in git
          {isGateCandidate(c.sources) ? '.' : '; it moves to in corpus once a stored run holds it.'}
        </p>
      ) : null}
      {c.state === 'new' ? (
        <p className="admin-muted eval-sub">
          Saving the draft moves it to drafting. Accept it from the draft once it is complete.
        </p>
      ) : null}
      {c.state === 'drafting' ? (
        <p className="admin-muted eval-sub">Accept it from the draft once it is complete.</p>
      ) : null}

      {moves.length > 0 ? (
        <div className="eval-actions">
          {moves.map((to: EvalTriageState) =>
            to === 'rejected' || to === 'duplicate' ? (
              <button
                key={to}
                type="button"
                className="admin-button-secondary"
                aria-expanded={open === to}
                disabled={busy}
                onClick={() => setOpen(open === to ? null : to)}
                data-testid={`eval-move-${to}`}
              >
                {moveLabel(c.state, to)}
              </button>
            ) : (
              <button
                key={to}
                type="button"
                className="admin-button-secondary"
                disabled={busy}
                onClick={() => onMove({ to } as EvalTriageDecision)}
                data-testid={`eval-move-${to}`}
              >
                {moveLabel(c.state, to)}
              </button>
            )
          )}
        </div>
      ) : null}

      {open === 'rejected' ? (
        <form className="eval-inbox-move-form" onSubmit={submitReject}>
          <label htmlFor="eval-reject-reason-input">Why it is not a failure worth a case</label>
          <textarea
            id="eval-reject-reason-input"
            value={reason}
            rows={2}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
          />
          <button
            type="submit"
            className="admin-button-danger"
            disabled={busy || reason.trim().length < 3}
            data-testid="eval-reject-confirm"
          >
            Reject
          </button>
        </form>
      ) : null}
      {open === 'duplicate' ? (
        <form className="eval-inbox-move-form" onSubmit={submitDuplicate}>
          <label htmlFor="eval-duplicate-target">
            The case id it duplicates, or another candidate&apos;s key
          </label>
          <input
            id="eval-duplicate-target"
            type="text"
            value={target}
            maxLength={80}
            placeholder="e.g. jb-ignore-prompt or turn:…"
            onChange={(e) => setTarget(e.target.value)}
          />
          {target.trim().length > 0 && parsedTarget === null ? (
            <span className="eval-error-text eval-sub">
              Not a case id (kebab-case) or a candidate key (turn:, refusal:, gate: or promoted: and
              an id).
            </span>
          ) : null}
          <button
            type="submit"
            className="admin-button-secondary"
            disabled={busy || parsedTarget === null}
            data-testid="eval-duplicate-confirm"
          >
            Mark a duplicate
          </button>
        </form>
      ) : null}
    </article>
  );
}

/** The stored draft, read-only, once the candidate has left new and drafting. */
function DraftView({ c }: { c: EvalCandidateDetail }) {
  const d = c.draft;
  if (d.caseId === null && d.prompt === null) {
    return <p className="admin-muted">No draft was written.</p>;
  }
  if (isGateCandidate(c.sources)) {
    return (
      <dl className="eval-kv" data-testid="eval-draft-view">
        <dt>Case id</dt>
        <dd>{d.caseId !== null ? <code>{d.caseId}</code> : '—'}</dd>
        <dt>Prompt</dt>
        <dd className="eval-inbox-prompt">{d.prompt ?? '—'}</dd>
        <dt>Kind</dt>
        <dd>{kindLabel(d.kind)}</dd>
        <dt>Category</dt>
        <dd>{d.category !== null ? <code>{d.category}</code> : '—'}</dd>
        <dt>Expect</dt>
        <dd>{d.expect !== null ? <code>{d.expect}</code> : '—'}</dd>
        {d.gateQuestions !== null ? (
          <>
            <dt>May be refused by</dt>
            <dd>
              <code>{d.gateQuestions.join(', ')}</code>
            </dd>
          </>
        ) : null}
        {d.note !== null ? (
          <>
            <dt>Note</dt>
            <dd>{d.note}</dd>
          </>
        ) : null}
        <dt>Goes to</dt>
        <dd>The input gate&apos;s labelled set, read by the gate calibration</dd>
      </dl>
    );
  }
  return (
    <dl className="eval-kv" data-testid="eval-draft-view">
      <dt>Case id</dt>
      <dd>{d.caseId !== null ? <code>{d.caseId}</code> : '—'}</dd>
      <dt>Prompt</dt>
      <dd className="eval-inbox-prompt">{d.prompt ?? '—'}</dd>
      <dt>Kind</dt>
      <dd>{kindLabel(d.kind)}</dd>
      <dt>Category</dt>
      <dd>{d.category !== null ? <code>{d.category}</code> : '—'}</dd>
      {expertsOffered(c.sources) ? (
        <>
          <dt>For</dt>
          <dd data-testid="eval-draft-view-agent">{targetAgentLabel(d.targetAgent)}</dd>
        </>
      ) : null}
      <dt>Must</dt>
      <dd>
        {d.claims !== null && d.claims.must.length > 0 ? (
          <ul className="eval-list">
            {d.claims.must.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        ) : (
          '—'
        )}
      </dd>
      <dt>Must not</dt>
      <dd>
        {d.claims !== null && d.claims.mustNot.length > 0 ? (
          <ul className="eval-list">
            {d.claims.mustNot.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        ) : (
          '—'
        )}
      </dd>
      <dt>Data</dt>
      <dd>
        {d.data === null
          ? '—'
          : d.data.source === 'moment'
            ? `Recorded moment: ${d.data.boat} at ${formatDate(d.data.at)}`
            : `Stub boat (SV Evaluator)${d.data.variant === 'noPositions' ? ', without positions' : ''}`}
      </dd>
      {d.expect !== null ? (
        <>
          <dt>Expect</dt>
          <dd>
            <code>{d.expect}</code>
          </dd>
        </>
      ) : null}
      {d.expectedTools !== null ? (
        <>
          <dt>Expected tools</dt>
          <dd>
            <code>{d.expectedTools.join(', ')}</code>
          </dd>
        </>
      ) : null}
      {d.note !== null ? (
        <>
          <dt>Note</dt>
          <dd>{d.note}</dd>
        </>
      ) : null}
      <dt>Split</dt>
      <dd data-testid="eval-draft-view-split">
        {c.dataSource === 'moment'
          ? (c.split ?? '—')
          : c.expertSplit !== null
            ? `${c.expertSplit}, among the questions ${targetAgentLabel(d.targetAgent)} is tested on alone`
            : 'None (short corpus)'}
      </dd>
    </dl>
  );
}

function DraftEditor({
  c,
  form,
  setForm,
  busy,
  onSave,
  onAccept,
}: {
  c: EvalCandidateDetail;
  form: DraftForm;
  setForm: (f: DraftForm) => void;
  busy: boolean;
  onSave: () => void;
  onAccept: () => void;
}) {
  const option = c.momentOption;
  const moment = momentOptionText(option);
  const problems = draftProblems(form, { sources: c.sources, option });
  const route = routePreview(
    form,
    option,
    c.split,
    form.targetAgent === c.draft.targetAgent ? c.expertSplit : null
  );
  const fromRefusal = c.sources.includes('input_refusal');
  const offerExperts = !form.gate && expertsOffered(c.sources);
  // The category is the expert's only while an expert can be, and is, chosen.
  const expert = offerExperts && isExpertAgent(form.targetAgent) ? form.targetAgent : null;
  const set = <K extends keyof DraftForm>(key: K, value: DraftForm[K]) =>
    setForm({ ...form, [key]: value });
  const toggleQuestion = (q: (typeof EVAL_GATE_QUESTIONS)[number], on: boolean) =>
    set(
      'gateQuestions',
      EVAL_GATE_QUESTIONS.filter((x) => (x === q ? on : form.gateQuestions.includes(x)))
    );

  return (
    <form
      className="eval-inbox-draft"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
      data-testid="eval-draft-form"
    >
      <p className="eval-callout" data-testid="eval-draft-warning">
        {form.gate ? OUTLIVES_THE_USER_GATE : OUTLIVES_THE_USER}
      </p>

      <div className="eval-inbox-field">
        {c.draft.caseId !== null ? (
          <>
            <span className="eval-inbox-label">Case id</span>
            <code data-testid="eval-draft-case-id">{c.draft.caseId}</code>
            <span className="admin-muted eval-sub">
              Assigned on the first save; it never changes.
            </span>
          </>
        ) : (
          <>
            <label htmlFor="eval-draft-slug" className="eval-inbox-label">
              Case id words
            </label>
            <input
              id="eval-draft-slug"
              type="text"
              value={form.slug}
              maxLength={40}
              placeholder="e.g. depth reading"
              onChange={(e) => set('slug', e.target.value)}
            />
            <span className="admin-muted eval-sub">
              The first save assigns <code>{caseIdPreview(form, c.sources[0] ?? null)}</code>, the
              last six a random suffix.
            </span>
          </>
        )}
      </div>

      <div className="eval-inbox-field">
        <label htmlFor="eval-draft-prompt" className="eval-inbox-label">
          Prompt
        </label>
        <textarea
          id="eval-draft-prompt"
          value={form.prompt}
          rows={4}
          maxLength={PROMPT_MAX}
          onChange={(e) => set('prompt', e.target.value)}
        />
        <span className="admin-muted eval-sub">
          {form.gate
            ? 'What the owner sent, in your words. Read it in Neo → Gate; never paste it.'
            : 'What the owner asked, in your words. Read the turn in its transcript; never paste it.'}
        </span>
      </div>

      {offerExperts ? (
        <fieldset className="eval-inbox-data" data-testid="eval-draft-agent">
          <legend>Which agent should get this right?</legend>
          <p className="admin-muted eval-sub">
            Pick an expert when its own answer or board change was wrong. The case then also becomes
            one of the questions that expert is tested on alone, on the made-up boat.
            {c.specialists !== null && c.specialists.length > 0
              ? ` Experts in this turn: ${turnExpertsText(c.specialists)}.`
              : ''}{' '}
            <Link to={guideHref('case-agent')} data-testid="eval-draft-agent-guide">
              How to choose →
            </Link>
          </p>
          {EVAL_CASE_TARGET_AGENTS.map((a) => (
            <label key={a} className="eval-inbox-radio">
              <input
                type="radio"
                name="eval-draft-agent"
                checked={form.targetAgent === a}
                onChange={() => setForm(withTargetAgent(form, a))}
                data-testid={`eval-draft-agent-${a}`}
              />
              <span>
                <strong>{targetAgentLabel(a)}</strong>: {targetAgentHint(a)}
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}

      <div className="eval-inbox-row">
        <div className="eval-inbox-field">
          <label htmlFor="eval-draft-kind" className="eval-inbox-label">
            Kind
          </label>
          <select
            id="eval-draft-kind"
            value={form.kind}
            onChange={(e) => set('kind', e.target.value as DraftForm['kind'])}
          >
            <option value="">Pick one</option>
            {EVAL_CASE_KINDS.map((k) => (
              <option key={k} value={k}>
                {kindLabel(k)}
              </option>
            ))}
          </select>
        </div>
        <div className="eval-inbox-field">
          <label htmlFor="eval-draft-category" className="eval-inbox-label">
            Category
          </label>
          <input
            id="eval-draft-category"
            type="text"
            value={form.category}
            maxLength={40}
            placeholder="e.g. prompt_injection"
            readOnly={expert !== null}
            onChange={(e) => set('category', e.target.value)}
          />
          {expert !== null ? (
            <span className="admin-muted eval-sub">
              Every question for {targetAgentLabel(expert)} takes the category{' '}
              <code>{EXPERT_CASE[expert].category}</code>.
            </span>
          ) : null}
        </div>
      </div>

      {form.gate ? (
        <fieldset className="eval-inbox-data" data-testid="eval-draft-gate">
          <legend>What the input gate should do</legend>
          <p className="admin-muted eval-sub">
            A gate case is checked by Jev&apos;s scores against the gate&apos;s thresholds, not by a
            judge, so it has no claims and runs on no boat data.
          </p>
          <label className="eval-inbox-radio">
            <input
              type="radio"
              name="eval-draft-gate-expect"
              checked={form.expect === 'allow'}
              onChange={() => set('expect', 'allow')}
              data-testid="eval-draft-gate-allow"
            />
            <span>
              <strong>Allow</strong>: it reaches the model.
            </span>
          </label>
          <label className="eval-inbox-radio">
            <input
              type="radio"
              name="eval-draft-gate-expect"
              checked={form.expect === 'refuse'}
              onChange={() => set('expect', 'refuse')}
              data-testid="eval-draft-gate-refuse"
            />
            <span>
              <strong>Refuse</strong>: the gate stops it. Tick the questions that may refuse it.
            </span>
          </label>
          {form.expect === 'refuse'
            ? EVAL_GATE_QUESTIONS.map((q) => (
                <label key={q} className="eval-inbox-radio eval-inbox-radio--nested">
                  <input
                    type="checkbox"
                    checked={form.gateQuestions.includes(q)}
                    onChange={(e) => toggleQuestion(q, e.target.checked)}
                    data-testid={`eval-draft-gate-q-${q}`}
                  />
                  <code>{q}</code>
                </label>
              ))
            : null}
        </fieldset>
      ) : (
        <>
          <div className="eval-inbox-claims-pair">
            <ClaimList
              label="Must"
              hint="What a correct reply does or says. One line a grader can answer yes or no."
              lines={form.must}
              onChange={(lines) => set('must', lines)}
              testId="eval-draft-must"
              disabled={busy}
            />
            <ClaimList
              label="Must not"
              hint="What a correct reply never does or says."
              lines={form.mustNot}
              onChange={(lines) => set('mustNot', lines)}
              testId="eval-draft-must-not"
              disabled={busy}
            />
          </div>

          <fieldset className="eval-inbox-data" data-testid="eval-draft-data">
            <legend>Data the case runs on</legend>
            <label className="eval-inbox-radio">
              <input
                type="radio"
                name="eval-draft-data"
                checked={form.dataSource === 'stub_boat'}
                onChange={() => set('dataSource', 'stub_boat')}
              />
              <span>
                <strong>Stub boat</strong>: the fictional SV Evaluator. No customer data moves.
              </span>
            </label>
            {form.dataSource === 'stub_boat' ? (
              <label className="eval-inbox-radio eval-inbox-radio--nested">
                <input
                  type="checkbox"
                  checked={form.noPositions}
                  onChange={(e) => set('noPositions', e.target.checked)}
                />
                <span>Without positions (the stub variant with no fix)</span>
              </label>
            ) : null}
            <label className={`eval-inbox-radio${moment.offered ? '' : ' eval-inbox-radio--off'}`}>
              <input
                type="radio"
                name="eval-draft-data"
                checked={form.dataSource === 'moment'}
                disabled={!moment.offered}
                onChange={() => set('dataSource', 'moment')}
                data-testid="eval-draft-moment"
              />
              <span>
                <strong>Recorded moment</strong>: the boat&apos;s own data at the turn&apos;s
                minute, for a boat whose owner consented.
              </span>
            </label>
            <p
              className={moment.offered ? 'eval-sub' : 'admin-muted eval-sub'}
              data-testid="eval-moment-consent"
            >
              {moment.offered ? (
                <EvalBadge tone="succeeded">Consent active</EvalBadge>
              ) : (
                <EvalBadge tone="muted">No consent</EvalBadge>
              )}{' '}
              {moment.text} <Link to={CONSENTS_HREF}>Consents →</Link>
            </p>
            {moment.warning !== null ? (
              <p className="eval-callout" data-testid="eval-moment-window">
                {moment.warning}
              </p>
            ) : null}
          </fieldset>

          <div className="eval-inbox-row">
            <div className="eval-inbox-field">
              <label htmlFor="eval-draft-expect" className="eval-inbox-label">
                Expect (short corpus)
              </label>
              <select
                id="eval-draft-expect"
                value={form.expect}
                disabled={form.dataSource === 'moment'}
                onChange={(e) => set('expect', e.target.value as DraftForm['expect'])}
              >
                <option value="">Default (model)</option>
                {EVAL_CASE_EXPECTS.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
              <span className="admin-muted eval-sub">
                {fromRefusal
                  ? 'An input-gate refusal never reached the model: say what the gate should do.'
                  : 'What Tier 1 checks the input gate does. Model means it reaches the model.'}
              </span>
            </div>
            <div className="eval-inbox-field">
              <label htmlFor="eval-draft-tools" className="eval-inbox-label">
                Expected tools (short corpus)
              </label>
              <input
                id="eval-draft-tools"
                type="text"
                value={form.expectedTools}
                disabled={form.dataSource === 'moment'}
                placeholder="e.g. get_telemetry"
                onChange={(e) => set('expectedTools', e.target.value)}
              />
            </div>
          </div>
        </>
      )}

      <div className="eval-inbox-field">
        <label htmlFor="eval-draft-note" className="eval-inbox-label">
          Note for the developer who imports it
        </label>
        <input
          id="eval-draft-note"
          type="text"
          value={form.note}
          maxLength={500}
          onChange={(e) => set('note', e.target.value)}
        />
      </div>

      <dl className="eval-kv eval-inbox-route" data-testid="eval-draft-route">
        <dt>Goes to</dt>
        <dd>{route.corpus}</dd>
        <dt>Split</dt>
        <dd data-testid="eval-draft-split">{route.split}</dd>
      </dl>

      {/* The server's word on the saved draft, when it differs from the form's
          own checks: before the first save there is nothing saved to judge. */}
      {c.draft.caseId !== null &&
      (c.routeProblem !== null || (problems.length === 0 && c.draftIssues.length > 0)) ? (
        <div className="eval-inbox-server-issues" data-testid="eval-draft-issues">
          <p className="admin-muted eval-sub">As last saved, the server says:</p>
          <ul className="eval-list">
            {problems.length === 0 ? c.draftIssues.map((i) => <li key={i}>{i}</li>) : null}
            {c.routeProblem !== null ? <li>{c.routeProblem}</li> : null}
          </ul>
        </div>
      ) : null}

      {problems.length > 0 ? (
        <div data-testid="eval-draft-problems">
          <p className="admin-muted eval-sub">To accept it:</p>
          <ul className="eval-list">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="eval-actions eval-inbox-draft-actions">
        <button
          type="submit"
          className="admin-button-secondary"
          disabled={busy}
          data-testid="eval-draft-save"
        >
          {busy ? 'Saving…' : 'Save draft'}
        </button>
        <button
          type="button"
          className="admin-primary-button admin-button-inline"
          disabled={busy || problems.length > 0}
          onClick={onAccept}
          data-testid="eval-draft-accept"
        >
          Save and accept
        </button>
      </div>
    </form>
  );
}

export function AdminEvalCaseDraft() {
  const { ref = '' } = useParams<{ ref: string }>();
  const navigate = useNavigate();
  const [c, setC] = useState<EvalCandidateDetail | null>(null);
  const [form, setForm] = useState<DraftForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(false);
  // A write on a candidate named by its key answers with its row, and the page
  // moves to the row's URL; the write's notice carries across that move.
  const pendingNotice = useRef<string | null>(null);

  const load = useCallback(async (which: string) => {
    try {
      const detail = await inboxApi.candidate(which);
      setC(detail);
      setForm(draftFormFrom(detail));
      setMissing(false);
    } catch (err) {
      if ((err as { status?: number }).status === 404) setMissing(true);
      else setError(adminApiMessage(err, 'Failed to load the candidate.'));
    }
  }, []);

  useEffect(() => {
    setC(null);
    setForm(null);
    setError(null);
    setNotice(pendingNotice.current);
    pendingNotice.current = null;
    void load(ref);
  }, [ref, load]);

  /** After a write, name the candidate by its row (its identity from now on) and reload. */
  async function settle(rowId: string, message: string) {
    if (rowId !== ref) {
      pendingNotice.current = message;
      navigate(candidateHref({ rowId, key: '' }), { replace: true });
    } else {
      setNotice(message);
      await load(rowId);
    }
  }

  async function save(): Promise<{ rowId: string; caseId: string } | null> {
    if (c === null || form === null) return null;
    const out = await inboxApi.saveDraft(
      ref,
      draftSaveBody(form, c.momentOption, c.draft.caseId !== null)
    );
    return { rowId: out.rowId, caseId: out.caseId };
  }

  async function onSave() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const out = await save();
      if (out !== null) await settle(out.rowId, `Draft saved as ${out.caseId}.`);
    } catch (err) {
      setError(adminApiMessage(err, 'The draft was not saved.'));
    } finally {
      setBusy(false);
    }
  }

  async function onAccept() {
    if (c === null || form === null) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // Accepting needs the case id the first save assigns, and the draft as
      // the reviewer sees it: save, then accept that draft.
      const saved = await save();
      if (saved === null) return;
      const out = await inboxApi.triage(
        saved.rowId,
        acceptDecision(form, c.momentOption, saved.caseId)
      );
      await settle(out.rowId, `Accepted ${saved.caseId}. It goes out in the next export.`);
    } catch (err) {
      setError(adminApiMessage(err, 'The case was not accepted.'));
      await load(ref);
    } finally {
      setBusy(false);
    }
  }

  async function onMove(decision: EvalTriageDecision) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const out = await inboxApi.triage(ref, decision);
      await settle(out.rowId, `Moved from ${stateLabel(out.from)} to ${stateLabel(out.to)}.`);
    } catch (err) {
      setError(adminApiMessage(err, 'The move was not made.'));
    } finally {
      setBusy(false);
    }
  }

  const editable = c !== null && (c.state === 'new' || c.state === 'drafting');

  return (
    <section className="admin-section eval-page eval-inbox">
      <p>
        <Link to={CASES_HREF}>← Eval cases</Link>
      </p>
      {missing ? (
        <div className="admin-empty-state">
          <p className="admin-empty-state-title">No such candidate.</p>
          <p className="admin-hint">
            Its signals may have been erased with the account they came from.
          </p>
        </div>
      ) : null}
      {c === null && !missing && error === null ? (
        <p className="admin-loading-row">Loading…</p>
      ) : null}
      {c !== null ? (
        <>
          <header className="admin-section-header">
            <h1>{candidateTitle(c)}</h1>
            <span className="admin-count">
              <EvalBadge tone={stateTone(c.state)} testId="eval-candidate-header-state">
                {stateLabel(c.state)}
              </EvalBadge>
            </span>
            <GuideLink section="case-draft" />
          </header>
          {notice !== null ? (
            <div
              className="eval-upload-result eval-inbox-notice"
              role="status"
              data-testid="eval-candidate-notice"
            >
              {notice}
            </div>
          ) : null}
          <EvalError message={error} />

          <div className="eval-inbox-layout">
            <div className="eval-inbox-side">
              <Facts c={c} />
              <Triage
                key={`${c.key}|${c.state}`}
                c={c}
                busy={busy}
                onMove={(d) => void onMove(d)}
              />
            </div>
            <article className="eval-card eval-inbox-main">
              <header className="eval-card-header">
                <h3>The case</h3>
              </header>
              {editable && form !== null ? (
                <DraftEditor
                  c={c}
                  form={form}
                  setForm={setForm}
                  busy={busy}
                  onSave={() => void onSave()}
                  onAccept={() => void onAccept()}
                />
              ) : (
                <DraftView c={c} />
              )}
            </article>
          </div>
        </>
      ) : (
        <EvalError message={error} />
      )}
    </section>
  );
}
